import {Component, Input, Optional} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {Base64 as base64} from 'js-base64';
import * as localForage from 'localforage';

import {Translatable, TranslationService} from '@ngmedax/translation';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {ConfigService} from '@ngmedax/config';
import {LayoutService} from '@ngmedax/layout';

import {ApiService} from '../../services/api.service';
import {AuthUriService} from '../../services/auth-uri.service';
import {TRANSLATION_GRID_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';


// hack to inject decorator declarations. must occur before class declaration!
export interface GridPreviewComponent extends Translatable {}

/**
 * Questionnaire preview for the grid. Adapted copy of the editor preview
 * component (kept independent on purpose): loads the full questionnaire from
 * the api first, because grid rows only carry metadata. The questionnaire
 * comes from the api already in the packed storage format, so no format
 * service is needed here. Works for immutable thieme questionnaires as well,
 * which cannot be opened in the editor.
 */
@Component({
  selector: 'app-qa-grid-preview',
  templateUrl: './grid-preview.component.html',
  styleUrls: ['./grid-preview.component.css']
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class GridPreviewComponent {
  @Input() questionnaire: Questionnaire;
  previewUrl: SafeResourceUrl;

  /**
   * Injects dependencies
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    private apiService: ApiService,
    private authUriService: AuthUriService,
    private configService: ConfigService,
    private layoutService: LayoutService,
    private http: HttpClient,
    private modalService: NgbModal,
    public sanitizer: DomSanitizer
  ) {
    localForage.config({driver: localForage.INDEXEDDB, name: 'myMedaxPreviewStorage'});
  }

  /**
   * Opens preview for the questionnaire of this grid row
   *
   * @param content
   */
  public async open(content) {
    if (!this.questionnaire) {
      return;
    }

    let key = '';

    try {
      this.layoutService.showPreloader();

      // grid rows only carry metadata - load the full questionnaire first
      const questionnaire = await this.apiService.loadQuestionnaire(this.questionnaire.id);
      const payload = {$schema: 'questionnaire-schema.json', questionnaire: questionnaire};

      key = base64.encode(questionnaire.id);
      const assetCdnUrl = this.configService.get('apis.asset.cdn');

      const isAutoThemingActive = await this.getSettingsValue('mobileThemingActive');
      const themeUrl = isAutoThemingActive ? `${assetCdnUrl}/mobile-theme/theme.zip` : this.configService.get('mobile.theme');

      await localForage.clear();
      await localForage.setItem('previewMode', 'true');
      await localForage.setItem(key, base64.encode(JSON.stringify(payload)));

      await localForage.removeItem('themeUrl');
      themeUrl && await localForage.setItem('themeUrl', themeUrl);
      await localForage.setItem('assetCdnUrl', assetCdnUrl);
      const locale = this.translationService ? this.translationService.getLocale() : 'de_DE';

      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`/assets/preview/?key=${key}&locale=${locale}`);
      this.layoutService.hidePreloader();
    } catch (error) {
      this.layoutService.hidePreloader();
      console.error(error);
      alert(this._(KEYS.GRID.ERROR_LOADING_PREVIEW));
      return;
    }

    this.modalService.open(content, {size: 'lg'}).result.finally(() => {
      key && localForage.removeItem(key).catch(e => console.error(e));
    });
  }

  /**
   * Returns a settings value from the settings api (minimal copy of the editor
   * settings service, only the read path). Returns null when the settings api
   * is not configured or the call fails.
   *
   * @param {string} key
   * @returns {Promise<any>}
   */
  private async getSettingsValue(key: string): Promise<any> {
    const settingsUri = this.authUriService.buildUrl('apis.settings.uri');

    if (!settingsUri) {
      return null;
    }

    const headers = this.authUriService.getAuthHeaders();

    try {
      const data: any = await new Promise((resolve, reject) => {
        this.http.post(settingsUri, {key: key}, {headers}).subscribe(resolve, reject);
      });

      if (!data || typeof data.value === 'undefined') {
        return null;
      }

      try {
        return JSON.parse(data.value);
      } catch (ignore) {
        return data.value;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
